/**
 * Customizer main entry — wires UI controls, IDB persistence, live preview,
 * and the export button.
 */
import { type DesignSystem, type MaterialName, defaultDesignSystem, migrate, tokensToCss } from './state.js';
import { loadCurrent, saveCurrent, pushHistory, debounce } from './idb.js';
import { exportPackage, type ExportFlavor } from './export.js';
import { validatePrefix } from '../../scripts/substitute-prefix.mjs';
import { setIconSet, type IconSet } from '../../src/ts/core/ry-icons.js';
// Side-effect import: register the Lucide set so the customizer can flip to
// it live. Fomantic is already registered by ry-ui.js's main entry.
import '../../src/ts/icons/lucide.js';

// Common icons shown in the preview tile. Names exist in both shipped sets.
const PREVIEW_ICONS = [
  'home', 'user', 'settings', 'search', 'heart', 'star', 'bookmark', 'mail',
  'calendar', 'clock', 'edit', 'trash', 'plus', 'minus', 'check', 'close',
  'chevron-down', 'chevron-up', 'chevron-left', 'chevron-right',
] as const;

/**
 * Set or clear the page's <link rel="icon"> live so a user's favicon upload
 * shows up in the browser tab immediately, and mirror it into the preview
 * column (simulated browser tab + chip). Empty URL clears both.
 */
function applyFavicon(url: string): void {
  const id = 'ry-customizer-favicon';
  let link = document.getElementById(id) as HTMLLinkElement | null;
  if (!url) {
    if (link) link.remove();
  } else {
    if (!link) {
      link = document.createElement('link');
      link.id = id;
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = url;
  }

  // Mirror into preview slots — replace placeholder spans with <img> and back.
  for (const sel of ['#favicon-preview-tab', '#favicon-preview-chip']) {
    const slot = document.querySelector(sel);
    if (!slot) continue;
    const parent = slot.parentElement;
    if (!parent) continue;
    if (url) {
      const img = document.createElement('img');
      img.id = slot.id;
      img.src = url;
      img.alt = '';
      parent.replaceChild(img, slot);
    } else {
      const span = document.createElement('span');
      span.id = slot.id;
      span.className = 'cz-favicon-placeholder';
      parent.replaceChild(span, slot);
    }
  }
}

function applyIconSet(set: IconSet): void {
  setIconSet(set);
  renderIconPreview();
}

function renderIconPreview(): void {
  const host = document.getElementById('icon-preview');
  if (!host) return;
  host.innerHTML = PREVIEW_ICONS.map(
    (n) => `<div class="icon-cell"><ry-icon name="${n}" size="24"></ry-icon><span>${n}</span></div>`
  ).join('');
}

// Library version — read from window at runtime so the customizer doesn't
// need to be rebuilt on every release.
const LIB_VERSION = (window as any).__RY_UI_VERSION__ ?? '0.0.0';

let state: DesignSystem;
let exportFlavor: ExportFlavor = 'modular';
let isHydrating = false;
let isSyncingDarkInputs = false;

// All palette colors that can have a dark-mode per-color override.
const DARK_OVERRIDABLE_COLORS = [
  'primary', 'secondary', 'accent', 'bg', 'text',
  'success', 'warning', 'danger', 'info',
  'red', 'orange', 'yellow', 'olive', 'green', 'teal', 'blue',
  'violet', 'purple', 'pink', 'brown', 'grey', 'black',
] as const;
type DarkOverridableColor = typeof DARK_OVERRIDABLE_COLORS[number];

/**
 * Push the current state into the dark-mode override inputs:
 *   - if user has set an explicit override, show that value
 *   - otherwise show the LIGHT value as a sensible starting point
 * The inputs ignore their own ry:input events while this is running so
 * the visual pre-fill doesn't get accidentally promoted to an override.
 */
function syncDarkInputsFromState(): void {
  isSyncingDarkInputs = true;
  for (const key of DARK_OVERRIDABLE_COLORS) {
    const el = document.querySelector(`#color-dark-${key}`) as RyControl | null;
    if (!el) continue;
    el.value = state.tokens.colorDark[key] ?? state.tokens.color[key];
  }
  setTimeout(() => { isSyncingDarkInputs = false; }, 0);
}

// Single source of truth for the color knob list. Keep in sync with
// ColorTokens in state.ts and the color rows in index.html.
const COLOR_IDS = [
  // Palette + neutrals
  'primary', 'secondary', 'accent', 'bg', 'text',
  // Semantic
  'success', 'warning', 'danger', 'info',
  // Extended palette (zevo @-variables: red/orange/yellow/olive/green/
  // teal/blue/violet/purple/pink/brown/grey/black)
  'red', 'orange', 'yellow', 'olive', 'green', 'teal', 'blue',
  'violet', 'purple', 'pink', 'brown', 'grey', 'black',
] as const;

/**
 * Suggested companion knob values per material. A material owns only surface +
 * edge; these are the radius/shadow it *recommends*. When `materialPropagate`
 * is on, picking a material writes these into the shape/depth knobs (and moves
 * the sliders). Turn the toggle off to pick a material without disturbing them.
 */
const MATERIAL_SUGGESTIONS: Record<MaterialName, { radiusBase: string; borderWidth: string; shadowStrength: number }> = {
  fomantic: { radiusBase: '0.5rem', borderWidth: '1px', shadowStrength: 1 }, // today's default
  tailwind: { radiusBase: '1rem',   borderWidth: '1px', shadowStrength: 0 }, // 16px corners, hairline, no shadow
};

const $ = <T extends Element = HTMLElement>(sel: string) => document.querySelector(sel) as T;

const debouncedAutosave = debounce(async (s: DesignSystem) => {
  await saveCurrent(s);
}, 400);
const debouncedHistory = debounce(async (s: DesignSystem) => {
  await pushHistory(s);
}, 2500);

/**
 * Apply the chosen mode (auto / light / dark) to <html>:
 *   - data-ry-mode for selectors that key off it
 *   - color-scheme inline style so `light-dark()` tokens resolve correctly
 *
 * 'auto' means "follow OS preference" — clear both so the browser picks.
 */
function applyMode(mode: string): void {
  const root = document.documentElement;
  // Suppress color/background transitions for one frame to avoid the
  // flash-of-mixed-state when the browser flips color-scheme but custom
  // properties interpolate.
  root.setAttribute('data-cz-mode-switching', '');
  if (mode === 'auto') {
    delete root.dataset.ryMode;
    root.style.removeProperty('color-scheme');
  } else {
    root.dataset.ryMode = mode;
    root.style.colorScheme = mode;
  }
  try { localStorage.setItem('ry-customizer-mode', mode); } catch { /* best effort */ }
  requestAnimationFrame(() => {
    requestAnimationFrame(() => root.removeAttribute('data-cz-mode-switching'));
  });
}

function applyLivePreview(s: DesignSystem): void {
  // The preview uses the canonical `ry-` prefix at runtime; the prefix only
  // matters at export time. So we generate a `:root { --ry-...: ...}` block
  // and inject it as a <style> element in the preview pane's scope.
  const css = tokensToCss(s, 'ry');
  let styleEl = document.getElementById('ry-live-tokens') as HTMLStyleElement | null;
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = 'ry-live-tokens';
    document.head.appendChild(styleEl);
  }
  styleEl.textContent = css;

  // Gradient + waves toggles — drive data attributes the page CSS keys off
  document.documentElement.setAttribute('data-ry-gradients', s.tokens.surface.useGradients ? 'on' : 'off');
  document.documentElement.setAttribute('data-ry-waves', s.tokens.surface.useWaves ? 'on' : 'off');
  // Material — drives [data-ry-material] which the shipped tokens key off
  document.documentElement.setAttribute('data-ry-material', s.tokens.surface.material);

  // Refresh dark override inputs — light-value defaults flow through here when
  // the user changes a light palette color and that color has no explicit
  // dark override yet.
  syncDarkInputsFromState();

  // Mirror to localStorage for the no-FOUC inline script on next page load
  try {
    localStorage.setItem('ry-customizer-css', css);
    localStorage.setItem('ry-customizer-gradients', s.tokens.surface.useGradients ? 'on' : 'off');
    localStorage.setItem('ry-customizer-waves', s.tokens.surface.useWaves ? 'on' : 'off');
    localStorage.setItem('ry-customizer-material', s.tokens.surface.material);
  } catch { /* quota / private mode — best effort */ }
}

function commit(): void {
  applyLivePreview(state);
  debouncedAutosave(state);
  debouncedHistory(state);
}

function update<K extends keyof DesignSystem['tokens']>(group: K, patch: Partial<DesignSystem['tokens'][K]>): void {
  state = {
    ...state,
    tokens: {
      ...state.tokens,
      [group]: { ...state.tokens[group], ...patch },
    },
    updatedAt: new Date().toISOString(),
  };
  commit();
}

// ───────────────────────────────────────────────────────────────────────
// Control wiring
// ───────────────────────────────────────────────────────────────────────

// All ry-ui form controls emit `ry:input` (live, as the user drags) and
// `ry:change` (final, on release). They expose a `.value` property; ry-slider
// returns a number, ry-color-picker and ry-select return strings.
type RyControl = HTMLElement & { value: any };

function wireControls(): void {
  // Prefix — still a native <input> in the topbar
  const prefixInput = $<HTMLInputElement>('#prefix-input');
  prefixInput.value = state.prefix;
  prefixInput.addEventListener('input', () => {
    const v = prefixInput.value.trim().toLowerCase();
    const err = validatePrefix(v);
    prefixInput.setCustomValidity(err ?? '');
    if (!err) {
      state = { ...state, prefix: v, updatedAt: new Date().toISOString() };
      debouncedAutosave(state);
    }
  });

  // Color knobs (ry-color-picker)
  for (const id of COLOR_IDS) {
    bindControl(`#color-${id}`, 'ry:input', (v) => update('color', { [id]: v } as any));
  }

  // Shape (ry-slider)
  bindControl('#radius-base', 'ry:input', (v: number) => update('shape', { radiusBase: `${v}rem` }));
  bindControl('#border-width', 'ry:input', (v: number) => update('shape', { borderWidth: `${v}px` }));

  // Density / Depth / Surface (ry-slider)
  bindControl('#space-scale', 'ry:input', (v: number) => update('density', { spaceScale: v }));
  bindControl('#shadow-strength', 'ry:input', (v: number) => update('depth', { shadowStrength: v }));
  bindControl('#shadow-spread', 'ry:input', (v: number) => update('depth', { shadowSpread: v }));
  bindControl('#panel-opacity', 'ry:input', (v: number) => update('surface', { panelOpacity: v }));
  bindControl('#panel-blur', 'ry:input', (v: number) => update('surface', { panelBlur: `${v}px` }));

  // Material — surface+edge preset. Picking one optionally propagates its
  // suggested radius/shadow into those knobs (and moves the sliders to match).
  bindControl('#material', 'ry:change', (v: string) => {
    const name = v as MaterialName;
    update('surface', { material: name });
    if (!state.tokens.surface.materialPropagate) return;
    const sug = MATERIAL_SUGGESTIONS[name];
    if (!sug) return;
    update('shape', { radiusBase: sug.radiusBase, borderWidth: sug.borderWidth });
    update('depth', { shadowStrength: sug.shadowStrength });
    // Reflect the applied suggestions on the sliders. Guard the scroll-to-tile
    // side effect while we set .value programmatically.
    isHydrating = true;
    setControl('#radius-base', parseRem(sug.radiusBase));
    setControl('#border-width', parsePx(sug.borderWidth));
    setControl('#shadow-strength', sug.shadowStrength);
    setTimeout(() => { isHydrating = false; }, 0);
  });
  bindControl('#material-propagate', 'ry:change', (v: string) => {
    update('surface', { materialPropagate: v === 'true' });
  });

  // Style preferences — ry-switch toggles
  bindControl('#use-gradients', 'ry:change', (v: string) => {
    update('surface', { useGradients: v === 'true' });
  });
  bindControl('#use-waves', 'ry:change', (v: string) => {
    update('surface', { useWaves: v === 'true' });
  });

  // Wave flourish params — only meaningful when useWaves is on
  bindControl('#wave-speed',     'ry:input', (v: number) => update('surface', { waveSpeed: v }));
  bindControl('#wave-amplitude', 'ry:input', (v: number) => update('surface', { waveAmplitude: v }));
  bindControl('#wave-color',     'ry:input', (v: string) => update('surface', { waveColor: v }));

  // Dark-mode derivation principles
  bindControl('#dark-brand-l', 'ry:input', (v: number) => update('dark', { brandLightness: v }));
  bindControl('#dark-brand-c', 'ry:input', (v: number) => update('dark', { brandChroma: v }));

  // Dark-mode per-color overrides — every color, light-value pre-filled.
  // An override is only stored if the user-entered value differs from the
  // current light value. Otherwise the auto-derivation formula applies.
  for (const key of DARK_OVERRIDABLE_COLORS) {
    const el = document.querySelector(`#color-dark-${key}`) as RyControl | null;
    if (!el) continue;
    el.addEventListener('ry:input', (e) => {
      if (isSyncingDarkInputs) return; // ignore our own programmatic sets
      const v = ((e as CustomEvent).detail?.value ?? '').trim();
      const lightValue = state.tokens.color[key];
      const next = { ...state.tokens.colorDark };
      if (v && v.toLowerCase() !== lightValue.toLowerCase()) {
        next[key] = v;
      } else {
        delete next[key];
      }
      state = { ...state, tokens: { ...state.tokens, colorDark: next }, updatedAt: new Date().toISOString() };
      commit();
    });
  }

  $('#dark-overrides-reset').addEventListener('click', () => {
    state = { ...state, tokens: { ...state.tokens, colorDark: {} }, updatedAt: new Date().toISOString() };
    commit();
    syncDarkInputsFromState();
  });

  // Motion
  bindControl('#animation-style', 'ry:change', (v: string) =>
    update('motion', { animationStyle: v as DesignSystem['tokens']['motion']['animationStyle'] })
  );
  bindControl('#duration-scale', 'ry:input', (v: number) => update('motion', { durationScale: v }));

  // Type — three separate font knobs
  bindControl('#font-display',   'ry:change', (v: string) => update('type', { fontDisplay: v }));
  bindControl('#font-primary',   'ry:change', (v: string) => update('type', { fontPrimary: v }));
  bindControl('#font-secondary', 'ry:change', (v: string) => update('type', { fontSecondary: v }));
  bindControl('#base-size',  'ry:input', (v: number) => update('type', { baseSize: `${v}px` }));
  bindControl('#scale-ratio', 'ry:input', (v: number) => update('type', { scaleRatio: v }));


  // Mode toggle — applies color-scheme inline so light-dark() values flip
  bindControl('#mode', 'ry:change', (v: string) => applyMode(v));

  // Icon set — native <select>; live preview + persists in state
  // Icon set — uses ry-select; bindControl wires scroll-on-interact via SCROLL_ANCHORS
  bindControl('#icon-set', 'ry:change', (v: string) => {
    update('icons', { set: v as IconSet });
    applyIconSet(v as IconSet);
  });

  // Brand — light logo, dark logo, favicon icon + clear button.
  // No wordmark: the logo IS the brand mark.
  const logoLight = $<HTMLInputElement>('#logo-light');
  const logoDark  = $<HTMLInputElement>('#logo-dark');
  const favicon   = $<HTMLInputElement>('#favicon');
  const logoClear = $<HTMLButtonElement>('#logo-clear');
  const readAsDataURL = (file: File): Promise<string> => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result ?? ''));
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  if (logoLight) {
    logoLight.addEventListener('change', async () => {
      const f = logoLight.files?.[0];
      if (!f) return;
      update('brand', { logoLight: await readAsDataURL(f) });
    });
  }
  if (logoDark) {
    logoDark.addEventListener('change', async () => {
      const f = logoDark.files?.[0];
      if (!f) return;
      update('brand', { logoDark: await readAsDataURL(f) });
    });
  }
  if (favicon) {
    favicon.addEventListener('change', async () => {
      const f = favicon.files?.[0];
      if (!f) return;
      const url = await readAsDataURL(f);
      update('brand', { icon: url });
      applyFavicon(url);
    });
  }
  if (logoClear) {
    logoClear.addEventListener('click', () => {
      update('brand', { logoLight: '', logoDark: '', icon: '' });
      if (logoLight) logoLight.value = '';
      if (logoDark) logoDark.value = '';
      if (favicon)  favicon.value  = '';
      applyFavicon('');
    });
  }
  // Restore favicon from saved state on boot
  if (state.tokens.brand.icon) applyFavicon(state.tokens.brand.icon);

  // Export format toggle
  bindControl('#export-flavor', 'ry:change', (v: string) => {
    exportFlavor = v as ExportFlavor;
  });

  // Collapsible knob groups — wire any <a class="cz-toggle" data-target="...">
  // to toggle the [hidden] attribute on the referenced element.
  // Persists open/closed state per group in localStorage.
  document.querySelectorAll<HTMLAnchorElement>('.cz-toggle').forEach((link) => {
    const targetId = link.dataset.target;
    if (!targetId) return;
    const target = document.getElementById(targetId);
    if (!target) return;
    const storageKey = `cz-collapse-${targetId}`;
    const stored = localStorage.getItem(storageKey);
    if (stored === 'open') target.hidden = false;
    if (stored === 'closed') target.hidden = true;
    link.textContent = target.hidden ? 'show' : 'hide';
    link.addEventListener('click', (e) => {
      e.preventDefault();
      target.hidden = !target.hidden;
      link.textContent = target.hidden ? 'show' : 'hide';
      localStorage.setItem(storageKey, target.hidden ? 'closed' : 'open');
    });
  });

  $('#export-button').addEventListener('click', async () => {
    const btn = $<HTMLButtonElement>('#export-button');
    btn.disabled = true;
    btn.textContent = 'Building…';
    try {
      await exportPackage(state, exportFlavor, LIB_VERSION);
      btn.textContent = 'Downloaded ✓';
      setTimeout(() => { btn.textContent = 'Export'; btn.disabled = false; }, 1500);
    } catch (err) {
      console.error('export failed', err);
      btn.textContent = 'Failed';
      btn.disabled = false;
    }
  });

  $('#reset-button').addEventListener('click', () => {
    if (!confirm('Reset all knobs to defaults? Current state will be saved to history.')) return;
    state = defaultDesignSystem(state.prefix);
    commit();
    hydrateAllControls();
  });

  $<HTMLInputElement>('#import-file').addEventListener('change', async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const incoming = migrate(JSON.parse(text));
      state = incoming;
      commit();
      hydrateAllControls();
    } catch (err) {
      alert('Failed to load JSON: ' + (err as Error).message);
    }
  });
}

function bindControl(sel: string, eventName: string, onChange: (value: any) => void): void {
  const el = document.querySelector(sel) as RyControl | null;
  if (!el) return;
  el.addEventListener(eventName, (e) => {
    const detail = (e as CustomEvent).detail;
    onChange(detail?.value);
  });
  // Scroll the preview to the tile this control affects at the START of an
  // interaction — pointerdown (mouse/touch) and keydown (keyboard arrows on
  // sliders / selects). One scroll per fresh interaction; the smooth-scroll
  // animation then runs in parallel with the user's drag.
  const anchor = SCROLL_ANCHORS[sel];
  if (anchor) {
    // Capture-phase: fires before ry-slider's thumb handler calls
    // stopPropagation(), so we still see drags that the inner component
    // would otherwise swallow.
    el.addEventListener('pointerdown', () => scrollPreviewTo(anchor), { capture: true });
    el.addEventListener('keydown', (e) => {
      const key = (e as KeyboardEvent).key;
      if (key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowUp' ||
          key === 'ArrowDown' || key === 'PageUp' || key === 'PageDown' ||
          key === 'Home' || key === 'End') {
        scrollPreviewTo(anchor);
      }
    }, { capture: true });
  }
}


// Map each control's selector to a preview-pane anchor element.
const SCROLL_ANCHORS: Record<string, string> = {
  '#color-primary':    '#swatch-palette',
  '#color-secondary':  '#swatch-palette',
  '#color-accent':     '#swatch-palette',
  '#color-bg':         '#swatch-neutrals',
  '#color-text':       '#swatch-neutrals',
  '#color-success':    '#swatch-semantic',
  '#color-warning':    '#swatch-semantic',
  '#color-danger':     '#swatch-semantic',
  '#color-info':       '#swatch-semantic',
  '#color-red':        '#swatch-extended',
  '#color-orange':     '#swatch-extended',
  '#color-yellow':     '#swatch-extended',
  '#color-olive':      '#swatch-extended',
  '#color-green':      '#swatch-extended',
  '#color-teal':       '#swatch-extended',
  '#color-blue':       '#swatch-extended',
  '#color-violet':     '#swatch-extended',
  '#color-purple':     '#swatch-extended',
  '#color-pink':       '#swatch-extended',
  '#color-brown':      '#swatch-extended',
  '#color-grey':       '#swatch-extended',
  '#color-black':      '#swatch-extended',
  '#font-display':     '#anchor-display',
  '#font-primary':     '#anchor-body',
  '#font-secondary':   '#anchor-body',
  '#base-size':        '#anchor-type',
  '#scale-ratio':      '#anchor-type',
  '#radius-base':      '#anchor-radii',
  '#border-width':     '#anchor-radii',
  '#shadow-strength':  '#anchor-shadows',
  '#shadow-spread':    '#anchor-shadows',
  '#panel-opacity':    '#anchor-shadows',
  '#panel-blur':       '#anchor-shadows',
  '#material':         '#anchor-components',
  '#animation-style':  '#anchor-motion',
  '#duration-scale':   '#anchor-motion',
  '#space-scale':      '#space-scale-row',
  '#icon-set':         '#anchor-icons',
  '#logo-light':       '#anchor-logo',
  '#logo-dark':        '#anchor-logo',
  '#favicon':          '#anchor-logo',
};

function scrollPreviewTo(selector: string): void {
  if (isHydrating) return;
  const target = document.querySelector(selector);
  if (!target) return;
  const behavior: ScrollBehavior =
    state.tokens.motion.animationStyle === 'none' ? 'instant' : 'smooth';
  target.scrollIntoView({ behavior, block: 'center' });
}

function setControl(sel: string, value: any): void {
  const el = document.querySelector(sel) as RyControl | null;
  if (el && value !== null && value !== undefined) el.value = value;
}

function setRySwitch(sel: string, on: boolean): void {
  const el = document.querySelector(sel) as HTMLElement | null;
  if (!el) return;
  if (on) el.setAttribute('checked', '');
  else    el.removeAttribute('checked');
}

function hydrateAllControls(): void {
  // Re-read state into every control. Used after import or reset.
  // Guard scroll-to-tile from firing while we programmatically set .value
  // on every ry-select / ry-color-input / ry-slider — those setters emit
  // ry:change synchronously, which would otherwise auto-scroll on boot.
  isHydrating = true;
  $<HTMLInputElement>('#prefix-input').value = state.prefix;

  for (const id of COLOR_IDS) {
    setControl(`#color-${id}`, state.tokens.color[id as keyof DesignSystem['tokens']['color']]);
  }
  setControl('#radius-base', parseRem(state.tokens.shape.radiusBase));
  setControl('#border-width', parsePx(state.tokens.shape.borderWidth));
  setControl('#space-scale', state.tokens.density.spaceScale);
  setControl('#shadow-strength', state.tokens.depth.shadowStrength);
  setControl('#shadow-spread', state.tokens.depth.shadowSpread);
  setControl('#material', state.tokens.surface.material);
  setRySwitch('#material-propagate', state.tokens.surface.materialPropagate);
  setControl('#panel-opacity', state.tokens.surface.panelOpacity);
  setControl('#panel-blur', parsePx(state.tokens.surface.panelBlur));
  setControl('#duration-scale', state.tokens.motion.durationScale);
  setControl('#base-size', parsePx(state.tokens.type.baseSize));
  setControl('#scale-ratio', state.tokens.type.scaleRatio);
  setControl('#animation-style', state.tokens.motion.animationStyle);
  setControl('#font-display', state.tokens.type.fontDisplay);
  setControl('#font-primary', state.tokens.type.fontPrimary);
  setControl('#font-secondary', state.tokens.type.fontSecondary);
  setControl('#dark-brand-l', state.tokens.dark.brandLightness);
  setControl('#dark-brand-c', state.tokens.dark.brandChroma);
  for (const key of ['primary', 'secondary', 'accent', 'bg', 'text'] as const) {
    setControl(`#color-dark-${key}`, state.tokens.colorDark[key] ?? '');
  }
  // ry-switch — driven by `checked` attribute (observedAttributes)
  setRySwitch('#use-gradients', state.tokens.surface.useGradients);
  setRySwitch('#use-waves',     state.tokens.surface.useWaves);
  // Wave flourish params
  setControl('#wave-speed',     state.tokens.surface.waveSpeed);
  setControl('#wave-amplitude', state.tokens.surface.waveAmplitude);
  setControl('#wave-color',     state.tokens.surface.waveColor);

  // Restore mode select from localStorage (set by inline head script pre-paint)
  let savedMode: string | null = null;
  try { savedMode = localStorage.getItem('ry-customizer-mode'); } catch { /* */ }
  setControl('#mode', savedMode ?? 'auto');

  // Icon set — hydrate via ry-select's value setter (goes through setControl).
  setControl('#icon-set', state.tokens.icons.set);
  applyIconSet(state.tokens.icons.set);

  // Brand favicon — file inputs can't be pre-filled, but re-apply the saved
  // data URL so the browser tab icon stays in sync after an import / reset.
  applyFavicon(state.tokens.brand.icon);

  // Pre-fill the dark per-color override inputs (every color, light value as hint)
  syncDarkInputsFromState();
  // Release the scroll guard on the next tick — after any synchronous
  // ry:change events from the .value setters have already fired.
  setTimeout(() => { isHydrating = false; }, 0);
}

// Motion preview — bind the toast buttons after boot
function wireMotionPreview(): void {
  const fire = (variant: 'info' | 'success' | 'warning' | 'error', message: string) => {
    const w = window as any;
    const ToastClass = w.RyToast ?? customElements.get('ry-toast');
    if (ToastClass?.[variant]) ToastClass[variant](message);
  };
  document.getElementById('motion-toast-info')?.addEventListener('click', () => fire('info', 'Tokens drive everything.'));
  document.getElementById('motion-toast-success')?.addEventListener('click', () => fire('success', 'Saved.'));
  document.getElementById('motion-toast-warning')?.addEventListener('click', () => fire('warning', 'Heads up.'));
  document.getElementById('motion-toast-error')?.addEventListener('click', () => fire('error', 'Something broke.'));
}

function parseRem(v: string): number | null {
  const m = v.match(/^([\d.]+)rem$/);
  return m ? parseFloat(m[1]!) : null;
}
function parsePx(v: string): number | null {
  const m = v.match(/^([\d.]+)px$/);
  return m ? parseFloat(m[1]!) : null;
}

// ───────────────────────────────────────────────────────────────────────
// Boot
// ───────────────────────────────────────────────────────────────────────

async function boot(): Promise<void> {
  state = await loadCurrent();
  applyLivePreview(state);
  wireControls();
  hydrateAllControls();
  wireMotionPreview();
}

boot().catch((err) => {
  console.error('customizer boot failed', err);
});
